/**
 * @fileoverview Terms & Conditions page.
 * @description Placeholder legal copy (see the on-page disclaimer) reachable from AuthLayout's
 * footer and from the other legal pages' cross-links. Composes LegalLayout with LegalDocCard for
 * the shared header/footer and card chrome, mirroring PrivacyPolicyPage's structure.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import LinkButton from '@/shared/components/atoms/LinkButton'
import LegalLayout from '@/shared/components/layouts/LegalLayout'
import LegalDocCard from '@/shared/components/molecules/LegalDocCard'
import { ROUTES } from '@/shared/constants/routes.constants'

const H2 = 'mt-8 mb-3 text-[17px] font-bold leading-snug text-ink first:mt-0'
const P = 'mb-3.5 text-[14.5px] leading-[1.75] text-slate'
const UL = 'mb-3.5 list-disc space-y-1.5 pl-5.5'
const LI = 'text-[14.5px] leading-[1.75] text-slate'

/**
 * @component
 */
function TermsAndConditionsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <LegalLayout>
      <LegalDocCard
        title={t('termsAndConditions.title')}
        updatedLabel={t('termsAndConditions.updated')}
        disclaimer={
          <>
            <strong className="font-semibold text-ink">
              {t('termsAndConditions.disclaimerBold')}
            </strong>{' '}
            {t('termsAndConditions.disclaimerRest')}
          </>
        }
      >
        <h2 className={H2}>{t('termsAndConditions.sections.acceptance.title')}</h2>
        <p className={P}>{t('termsAndConditions.sections.acceptance.body')}</p>

        <h2 className={H2}>{t('termsAndConditions.sections.description.title')}</h2>
        <p className={P}>{t('termsAndConditions.sections.description.body')}</p>

        <h2 className={H2}>{t('termsAndConditions.sections.eligibility.title')}</h2>
        <ul className={UL}>
          <li className={LI}>{t('termsAndConditions.sections.eligibility.item1')}</li>
          <li className={LI}>{t('termsAndConditions.sections.eligibility.item2')}</li>
          <li className={LI}>{t('termsAndConditions.sections.eligibility.item3')}</li>
        </ul>

        <h2 className={H2}>{t('termsAndConditions.sections.acceptableUse.title')}</h2>
        <p className={P}>{t('termsAndConditions.sections.acceptableUse.intro')}</p>
        <ul className={UL}>
          <li className={LI}>{t('termsAndConditions.sections.acceptableUse.item1')}</li>
          <li className={LI}>{t('termsAndConditions.sections.acceptableUse.item2')}</li>
          <li className={LI}>{t('termsAndConditions.sections.acceptableUse.item3')}</li>
          <li className={LI}>{t('termsAndConditions.sections.acceptableUse.item4')}</li>
          <li className={LI}>{t('termsAndConditions.sections.acceptableUse.item5')}</li>
        </ul>

        <h2 className={H2}>{t('termsAndConditions.sections.confidentiality.title')}</h2>
        <p className={P}>{t('termsAndConditions.sections.confidentiality.body')}</p>

        <h2 className={H2}>{t('termsAndConditions.sections.intellectualProperty.title')}</h2>
        <p className={P}>{t('termsAndConditions.sections.intellectualProperty.body')}</p>

        <h2 className={H2}>{t('termsAndConditions.sections.availability.title')}</h2>
        <p className={P}>{t('termsAndConditions.sections.availability.body')}</p>

        <h2 className={H2}>{t('termsAndConditions.sections.liability.title')}</h2>
        <p className={P}>{t('termsAndConditions.sections.liability.body')}</p>

        <h2 className={H2}>{t('termsAndConditions.sections.termination.title')}</h2>
        <p className={P}>{t('termsAndConditions.sections.termination.body')}</p>

        <h2 className={H2}>{t('termsAndConditions.sections.governingLaw.title')}</h2>
        <p className={P}>{t('termsAndConditions.sections.governingLaw.body')}</p>

        <h2 className={H2}>{t('termsAndConditions.sections.changes.title')}</h2>
        <p className={P}>{t('termsAndConditions.sections.changes.body')}</p>

        <h2 className={H2}>{t('termsAndConditions.sections.contact.title')}</h2>
        <p className={P}>
          {t('termsAndConditions.sections.contact.bodyPrefix')}{' '}
          <a
            href={`mailto:${t('termsAndConditions.sections.contact.emailLabel')}`}
            className="font-medium text-primary underline"
          >
            {t('termsAndConditions.sections.contact.emailLabel')}
          </a>{' '}
          {t('termsAndConditions.sections.contact.bodyMiddle')}{' '}
          <LinkButton size="sm" onClick={() => void navigate(ROUTES.CONTACT_SUPPORT)}>
            {t('legalNav.contactSupport')}
          </LinkButton>{' '}
          {t('termsAndConditions.sections.contact.bodySuffix')}
        </p>
      </LegalDocCard>
    </LegalLayout>
  )
}

export default TermsAndConditionsPage
